import { AlertTriangle, Gift } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { Skeleton } from "@/shared/ui/skeleton";

type ReportRow = components["schemas"]["ReportRow"];

interface ReportTableProps {
  items: ReportRow[];
  isLoading: boolean;
  isError?: boolean;
}

const TH =
  "h-9 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none";
const TD = "px-4 py-3 text-sm text-[var(--text-primary)] align-middle";

export function ReportTable({ items, isLoading, isError }: ReportTableProps): JSX.Element {
  const { t } = useTranslation("promotion");

  if (isError || (!isLoading && items.length === 0)) {
    return (
      <div
        className="rounded-2xl border border-[var(--premium-border)] bg-white"
        data-testid="report-table"
      >
        <EmptyState
          icon={<Gift className="h-5 w-5" aria-hidden />}
          title={t("report.empty_title")}
          body={t("report.empty_body")}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="space-y-2 rounded-2xl border border-[var(--premium-border)] bg-white p-4"
        data-testid="report-table-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--premium-border)] bg-white">
      <table className="w-full border-collapse" data-testid="report-table">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            <th className={TH}>{t("report.column.sku")}</th>
            <th className={TH}>{t("report.column.name")}</th>
            <th className={TH}>{t("report.column.marketplace")}</th>
            <th className={`${TH} text-right`}>{t("report.column.quota")}</th>
            <th className={`${TH} text-right`}>{t("report.column.used")}</th>
            <th className={`${TH} text-right`}>{t("report.column.remaining")}</th>
            <th className={TH}>{t("report.column.shop")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            // ⚠ low when <10% of quota remains (or only one unit left on a large quota).
            const low =
              row.quota > 0 &&
              (row.remaining < row.quota * 0.1 || (row.remaining === 1 && row.quota > 1));
            return (
              <tr
                key={`${row.sku}-${row.marketplace}`}
                data-testid={`report-row-${row.sku}`}
                className="border-b border-[var(--border-subtle)] last:border-0"
              >
                <td className={TD}>
                  <span className="font-mono text-[12.5px] font-semibold text-[var(--brand-700)]">
                    {row.sku}
                  </span>
                </td>
                <td className={`${TD} text-[var(--text-secondary)]`}>{row.sku_name}</td>
                <td className={TD}>
                  <MarketplaceChip marketplace={row.marketplace} />
                </td>
                <td className={`${TD} num text-right tabular-nums text-[var(--text-secondary)]`}>
                  {row.quota.toLocaleString()}
                </td>
                <td className={`${TD} num text-right tabular-nums text-[var(--text-secondary)]`}>
                  {row.used.toLocaleString()}
                </td>
                <td className={`${TD} num text-right tabular-nums`}>
                  <span
                    className={`inline-flex items-center justify-end gap-1 ${
                      low
                        ? "font-bold text-[var(--intent-warning-fg)]"
                        : "text-[var(--text-secondary)]"
                    }`}
                  >
                    {low && (
                      <AlertTriangle
                        className="h-3.5 w-3.5"
                        aria-label={t("report.column.remaining")}
                      />
                    )}
                    {row.remaining.toLocaleString()}
                  </span>
                </td>
                <td className={`${TD} text-[12.5px] text-[var(--text-secondary)]`}>{row.shop}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
